import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Grid,
  Alert,
  Tooltip,
  TextField,
  Autocomplete,
  Collapse,
  Switch,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
  LinkOff as DisconnectIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  OpenInFull as OpenInFullIcon,
  Close as CloseIcon,
  InfoOutlined as SpamAutoPushInfoIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { BusinessRulesValidationResult } from '../../services/businessRulesService';
import SignatureAndSalutation from '../SignatureAndSalutation';
import AddSignatureAndSalutationModal, { SignatureAndSalutationData } from '../AddSignatureAndSalutationModal';
import KeywordsMultiSelect from '../KeywordsMultiSelect';
import DescriptionCards from '../AddSpaceModal/DescriptionCards';
import BusinessRulesSection from './BusinessRulesSection';
import { useDescriptionHandlers } from '../../hooks/useDescriptionHandlers';
import EditSpaceModal from '../EditSpaceModal';
import {
  disconnectEmailAccount,
  reconnectEmailAccount,
  reauthorizeEmailAccount,
  deleteEmailData,
  subscribeToEmailSyncStatus,
  refreshEmailSyncStatus,
  EmailSyncStatusResponse,
  generateDescription,
} from '../../services/emailService';
import type { ReauthRequiredError } from '../../services/emailService';
import { EMAIL_SYNC, STORAGE_KEYS, SOCKET_KEYS, API_CONFIG, API_ENDPOINTS, ML_API_ENDPOINTS, GMAIL_CLIENT_ID, OFFICE365_CLIENT_ID } from '../../helpers/constants';
import { getFetchErrorMessage } from '../../helpers/errorUtils';
import { useToast } from '../../contexts/ToastContext';
import { formatRelativeTime, parseUTCToLocal } from '@/helpers/dateUtils';
import { formatSyncTimeWithDirection } from '@/helpers/date';
import { useNavigate } from 'react-router-dom';
import socketService from '../../services/socketService';
import { LanguageConstants } from '@/helpers/LanguageConstants';
import { useChannelAdmin } from '../../hooks/useChannelAdmin';
import { modalStyles } from '../../styles/modalStyles';
import { measureScrollbarWidth } from '../../helpers/domUtils';

interface EmailConfigTabProps {
  channelId?: string;
  channelName?: string;
  channelDescription?: string;
  /** Channel tags from channel API (e.g. category labels) - shown above Purpose of the channel */
  channelTags?: string[];
  /** Channel subtags from channel API (e.g. sub-category labels) - shown next to Channel tags */
  channelSubtags?: string[];
  channelBusinessRules?: string;
  isChannelArchived?: boolean;
  channelType?: 'email' | 'message' | 'voice';
  workspaceTag?: string;
  onClose?: () => void;
  /** When true, opens the channel Edit modal (e.g. from Automation tab empty state link) */
  openEditModalRequest?: boolean;
  onOpenEditModalRequestHandled?: () => void;
  /**
   * Called after a channel edit is successfully saved to the server. The parent (owner of the
   * real channelData state, e.g. ThreadDetailPage) should use this to trigger a clean API
   * re-fetch of channel details — otherwise this tab's own local optimistic state update is
   * lost on remount (tab switch / Settings drawer close+reopen), and the stale props this
   * component was given on mount reappear (ref: Channel Config stale-data-after-edit fix).
   */
  onChannelUpdated?: () => void;
  /** Business_rule_canvas branch (TDD §8.2) — forwarded to BusinessRulesSection's "See workflow
   * canvas" button. Omit to keep this tab's edit dialog exactly as it renders today. */
  onBuildWorkflow?: (rulesText: string) => void;
}

const DESCRIPTION_EXPAND_ICON_BASE_RIGHT = 8;
const DESCRIPTION_EXPAND_ICON_SCROLL_GAP = 8;

// Module-level (not React state) so it survives this tab remounting when the admin navigates
// away from Channel Config and back — the GET fetch that confirms the real value is still async,
// so without a cache the button always flashes its hardcoded default (Enable) for a moment first,
// every single time, even when we already know the real value from a fetch/toggle earlier this
// session. Keyed by channelId since a Settings drawer can be reopened against a different
// channel. Intentionally in-memory only (not localStorage) — it's just a last-known-value hint to
// avoid the flash; the mount-time fetch remains the source of truth and still runs every time.
const spamAutoPushCache = new Map<string, boolean>();

const TAG_OPTIONS = [
  'Sales Support', 'Customer Support', 'Business Operations Support',
  'Accounts', 'Billing', 'HR', 'Recruitment', 'IT support', 'Others',
];

// Safe UUID generator — crypto.randomUUID() requires a secure context (HTTPS/localhost).
// Falls back to a timestamp+random string so clicks never silently fail in edge environments.
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Normalizes channel_sub_tags from the API response to a flat string[].
// The field can be string[] (legacy) or Record<string,string[]> (new per-tag format).
function normalizeSubtags(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'object') {
    return Array.from(new Set(Object.values(value as Record<string, string[]>).flat()));
  }
  return [];
}

const EmailConfigTab: React.FC<EmailConfigTabProps> = ({ 
  channelId, 
  channelName, 
  channelDescription,
  channelTags: channelTagsProp,
  channelSubtags: channelSubtagsProp,
  channelBusinessRules,
  isChannelArchived = false,
  channelType,
  workspaceTag,
  onClose,
  openEditModalRequest = false,
  onOpenEditModalRequestHandled,
  onChannelUpdated,
  onBuildWorkflow,
}) => {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  
  // Check if current user is channel admin
  const { isChannelAdmin } = useChannelAdmin(channelId);
  
  const [emailSyncData, setEmailSyncData] = useState<EmailSyncStatusResponse['data'] | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [emailSyncError, setEmailSyncError] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<SignatureAndSalutationData[]>([]);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  // spam_auto_push_enabled — seeded synchronously from spamAutoPushCache (falls back to false/
  // off only when this channel has never been fetched/toggled this session) so remounting this
  // tab doesn't flash the wrong label before the mount-time fetch below resolves with the real
  // persisted value. See that effect's own comment for the true-persistence behavior and why it
  // reads both a nested and a top-level response shape.
  const [spamAutoPushEnabled, setSpamAutoPushEnabled] = useState(
    () => (channelId ? spamAutoPushCache.get(channelId) ?? false : false)
  );
  const [isTogglingSpamAutoPush, setIsTogglingSpamAutoPush] = useState(false);
  // 401-reauth recovery (revoked refresh token) — see docs/EmailReauth_401_Recovery_FE_Implementation.md
  const [reauthPromptOpen, setReauthPromptOpen] = useState(false);
  const [reauthMessage, setReauthMessage] = useState<string>('');
  const [reauthProvider, setReauthProvider] = useState<string>('');
  // mdifying_reconection_ui branch — reauthMessage is the backend's raw technical error text
  // (provider error codes, trace/correlation IDs, timestamps), not meant to be the primary
  // thing a user reads. Hidden behind this toggle so the dialog always leads with a clean,
  // friendly message; reset whenever the dialog closes so it doesn't stay expanded next time.
  const [showReauthDetails, setShowReauthDetails] = useState(false);
  // reauthPopupLoading tracks only "popup is open, waiting for the user" — cleared the moment
  // the popup closes (success or cancel), same as AddSpaceModal's gmailLoading/office365Loading.
  // isReauthorizing tracks the separate, longer-lived /reauthorize network call, which starts
  // only after the popup has already closed — must not share a flag with reauthPopupLoading or
  // the "closed" interval clears it before the request resolves (PR #594 review).
  const [reauthPopupLoading, setReauthPopupLoading] = useState(false);
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const reauthPopupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addSignatureModalOpen, setAddSignatureModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Edit channel modal state (for email channels with complex form)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSessionId, setEditSessionId] = useState(0);
  // Edit space modal state (for message channels - reusing EditSpaceModal)
  const [editSpaceModalOpen, setEditSpaceModalOpen] = useState(false);
  // Categories state (main tags) - matching AddSpaceModal
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // Per-tag subtags state: each category owns its own list of subtags
  const [selectedKeywordsPerTag, setSelectedKeywordsPerTag] = useState<Record<string, string[]>>({});
  // True when the edit modal was opened for a legacy channel whose flat subtags were distributed across all tags
  const [isLegacySubtagMigration, setIsLegacySubtagMigration] = useState(false);
  // tagRows: each row has a stable id (for React key) and the selected tag name
  const [tagRows, setTagRows] = useState<{ id: string; tag: string }[]>([{ id: generateId(), tag: '' }]);

  // Sync tagRows → selectedCategories (filter out empty slots)
  useEffect(() => {
    setSelectedCategories(tagRows.map(r => r.tag).filter(Boolean));
  }, [tagRows]);

  // Flat deduplicated subtag list — used for description generation and backward-compat save payload
  const flatSelectedKeywords = useMemo(
    () => Array.from(new Set(Object.values(selectedKeywordsPerTag).flat())),
    [selectedKeywordsPerTag],
  );

  // Mirrors the description-fetch effect's own categoriesArray/keywordsArray guard — used to
  // disable "View Suggestions" (with an explanatory tooltip) instead of letting a click
  // silently no-op when no tag+subtag is selected yet (PR #603 review, N1).
  const hasTagAndSubtagForSuggestions = selectedCategories.length > 0 && flatSelectedKeywords.length > 0;

  // Sync per-tag subtags when categories change (add empty array for new tags, drop removed ones)
  useEffect(() => {
    setSelectedKeywordsPerTag((prev) => {
      const next: Record<string, string[]> = {};
      for (const cat of selectedCategories) {
        next[cat] = prev[cat] ?? [];
      }
      return next;
    });
  }, [selectedCategories]);
  // Local channel tags/subtags after update so UI shows new values before parent refetches
  const [currentChannelTags, setCurrentChannelTags] = useState<string[] | null>(null);
  const [currentChannelSubtags, setCurrentChannelSubtags] = useState<string[] | null>(null);
  // Raw (un-flattened) channel_sub_tags from the API — preserves per-tag structure when the
  // backend already returns Record<string,string[]>. Used to seed the edit modal correctly;
  // currentChannelSubtags (flattened) is only for the read-only summary chips display.
  const [currentChannelSubtagsRaw, setCurrentChannelSubtagsRaw] = useState<string[] | Record<string, string[]> | null>(null);
  const [description, setDescription] = useState<string>('');
  const [editBusinessRules, setEditBusinessRules] = useState<string>('');
  const [descriptionSuggestions, setDescriptionSuggestions] = useState<string[]>([]);
  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  const [showDescriptionField, setShowDescriptionField] = useState(true);
  const [showDescriptionCards, setShowDescriptionCards] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [descriptionHasVerticalScroll, setDescriptionHasVerticalScroll] = useState(false);
  const [descriptionScrollbarWidth, setDescriptionScrollbarWidth] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [retrainDialogOpen, setRetrainDialogOpen] = useState(false);
  const [retrainLoading, setRetrainLoading] = useState(false);
  const descriptionAbortControllerRef = useRef<AbortController | null>(null);
  const descriptionRequestIdRef = useRef<number>(0);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Refs to track previous values for comparison (matching AddSpaceModal pattern)
  const previousSelectedCategoriesRef = useRef<string[]>([]);
  const previousSelectedKeywordsRef = useRef<string[]>([]);
  // Edit Channel dialog: the description-suggestions API must NOT fire just because the
  // dialog opened or because tags/subtags changed — only an explicit "View Suggestions"
  // click should call it. Bumping this nonce is that explicit trigger; the fetch effect
  // below no-ops unless the nonce has advanced past what it already handled, and
  // openChannelEditModal resets both back to 0 so a freshly-opened dialog starts un-armed.
  const [descriptionFetchNonce, setDescriptionFetchNonce] = useState(0);
  const handledDescriptionFetchNonceRef = useRef(0);
  // Bumped whenever the admin picks a description from the suggestion cards (Select or
  // inline Edit) — the one thing allowed to trigger a business-rules-suggestions refresh in
  // this dialog, per the same "nothing automatic" rule as descriptionFetchNonce above.
  const [businessRulesFetchNonce, setBusinessRulesFetchNonce] = useState(0);
  // Local state to track current channel description (updated without page refresh)
  const [currentDescription, setCurrentDescription] = useState<string>(channelDescription || '');
  // Local state to track current channel name (updated without page refresh)
  const [currentChannelName, setCurrentChannelName] = useState<string>(channelName || '');
  const [currentBusinessRules, setCurrentBusinessRules] = useState<string>(channelBusinessRules || '');
  const [businessRulesValidationStatus, setBusinessRulesValidationStatus] = useState<
    'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean'
  >('idle');
  const [businessRulesCanUpdate, setBusinessRulesCanUpdate] = useState<boolean>(false);
  const [businessRulesForceSave, setBusinessRulesForceSave] = useState<boolean>(false);
  const [businessRulesValidationResult, setBusinessRulesValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);
  const [serverValidationResult, setServerValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);

  // Update local description state when prop changes
  useEffect(() => {
    setCurrentDescription(channelDescription || '');
  }, [channelDescription]);

  // Update local channel name state when prop changes
  useEffect(() => {
    setCurrentChannelName(channelName || '');
  }, [channelName]);

  useEffect(() => {
    setCurrentBusinessRules(channelBusinessRules || '');
  }, [channelBusinessRules]);

  // Subscribe to the centralized email sync status service
  useEffect(() => {
    if (!channelName || channelType !== 'email') {
      setInitialLoading(false);
      return;
    }

    setInitialLoading(true);
    const token = localStorage.getItem('refreshToken') || '';

    const handleSyncStatusUpdate = (
      status: EmailSyncStatusResponse | null,
      error: Error | null
    ) => {
      setInitialLoading(false);
      if (error) {
        setEmailSyncError(getFetchErrorMessage(error, 'Failed to fetch sync status'));
        return;
      }
      if (status) {
        setEmailSyncError(null);
        setEmailSyncData(status.data);
      }
  };

    const unsubscribe = subscribeToEmailSyncStatus(
      channelName,
      token,
      handleSyncStatusUpdate,
      channelType
    );

    // Cleanup subscription on component unmount
    return () => {
      unsubscribe();
    };
  }, [channelName, channelType]);

  // Reset local tags/subtags when switching channel so we use fresh props
  useEffect(() => {
    setCurrentChannelTags(null);
    setCurrentChannelSubtags(null);
    setCurrentChannelSubtagsRaw(null);
  }, [channelId]);

  // Display tags/subtags: use local state after update, otherwise props (same source as channel list)
  const displayChannelTags = useMemo(
    () => currentChannelTags ?? channelTagsProp ?? [],
    [currentChannelTags, channelTagsProp]
  );
  const displayChannelSubtags = currentChannelSubtags ?? normalizeSubtags(channelSubtagsProp);
  // Raw (un-flattened) subtags — used only to seed the edit modal's per-tag state correctly.
  const rawChannelSubtags = currentChannelSubtagsRaw ?? ((channelSubtagsProp as unknown as (string[] | Record<string, string[]> | undefined)) ?? null);
  // True when channel_sub_tags is already in the per-tag Record format (not legacy flat array)
  const isPerTagSubtagFormat = !!rawChannelSubtags && !Array.isArray(rawChannelSubtags);
  // Groups subtags under their owning tag for the read-only summary display; empty when legacy flat format.
  // Includes tags with zero subtags so every channel tag still appears in the combined view.
  const subtagsGroupedByTag = useMemo(() => {
    if (!isPerTagSubtagFormat) return [];
    const rawMap = rawChannelSubtags as Record<string, string[]>;
    return displayChannelTags.map((tag) => ({ tag, subtags: rawMap[tag] ?? [] }));
  }, [isPerTagSubtagFormat, rawChannelSubtags, displayChannelTags]);

  const openChannelEditModal = useCallback(() => {
    setDescription(currentDescription || '');
    setEditBusinessRules(currentBusinessRules || '');
    setSelectedCategories(displayChannelTags);
    // channel_sub_tags can be a per-tag Record (already correctly assigned) or a flat
    // string[] (legacy channels, no per-tag grouping). Only the legacy shape needs the
    // migration behavior of copying all subtags into every tag.
    const isPerTagFormat = !!rawChannelSubtags && !Array.isArray(rawChannelSubtags);
    const initialPerTag: Record<string, string[]> = {};
    if (isPerTagFormat) {
      const rawMap = rawChannelSubtags as Record<string, string[]>;
      for (const tag of displayChannelTags) {
        initialPerTag[tag] = [...(rawMap[tag] ?? [])];
      }
    } else {
      // Legacy flat format: distribute all existing subtags to every tag so nothing is
      // dropped from the UI. The user can then remove subtags that don't belong before saving.
      for (const tag of displayChannelTags) {
        initialPerTag[tag] = [...displayChannelSubtags];
      }
    }
    setSelectedKeywordsPerTag(initialPerTag);
    // Show migration banner only for legacy flat subtags — per-tag data is already correct.
    setIsLegacySubtagMigration(!isPerTagFormat && displayChannelSubtags.length > 0);
    // Initialize rows from existing tags (or one empty row if none)
    setTagRows(displayChannelTags.length > 0 ? displayChannelTags.map(t => ({ id: generateId(), tag: t })) : [{ id: generateId(), tag: '' }]);
    previousSelectedCategoriesRef.current = displayChannelTags;
    // Seed with the SAME dedup/flatten transform flatSelectedKeywords uses (Set + flat over
    // selectedKeywordsPerTag's values), not displayChannelSubtags — that's a differently
    // ordered/deduplicated list, and a mismatched JSON.stringify order made keywordsChanged
    // spuriously true right on open (no real edit yet), which jumped straight to the
    // suggestion-cards view instead of showing the saved description in the field.
    previousSelectedKeywordsRef.current = Array.from(new Set(Object.values(initialPerTag).flat()));
    setDescriptionSuggestions([]);
    setShowDescriptionField(true);
    setShowDescriptionCards(false);
    setDescriptionFetchNonce(0);
    handledDescriptionFetchNonceRef.current = 0;
    setBusinessRulesFetchNonce(0);
    setEditSessionId((id) => id + 1);
    setEditModalOpen(true);
    setBusinessRulesValidationStatus('idle');
    setBusinessRulesCanUpdate(false);
    setBusinessRulesForceSave(false);
    setBusinessRulesValidationResult(null);
    setServerValidationResult(null);
  }, [
    currentDescription,
    currentBusinessRules,
    displayChannelTags,
    displayChannelSubtags,
    rawChannelSubtags,
  ]);

  const handleBusinessRulesValidationMetaChange = useCallback((meta: {
    status: 'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean';
    canUpdate: boolean;
    forceSave: boolean;
    result: BusinessRulesValidationResult | null;
  }) => {
    setBusinessRulesValidationStatus(meta.status);
    setBusinessRulesCanUpdate(meta.canUpdate);
    setBusinessRulesForceSave(meta.forceSave);
    setBusinessRulesValidationResult(meta.result);
    if (meta.status !== 'errors') {
      setServerValidationResult(null);
    }
  }, []);

  useEffect(() => {
    if (!openEditModalRequest || channelType !== 'email') return;
    openChannelEditModal();
    onOpenEditModalRequestHandled?.();
  }, [openEditModalRequest, channelType, openChannelEditModal, onOpenEditModalRequestHandled]);

  // Message channels have their own separate "Edit" surface — EditSpaceModal, opened via
  // editSpaceModalOpen below (not the email "Edit Channel" dialog above, which is unreachable
  // for message channels since they take the early-return branch further down). Lets an external
  // caller (e.g. a channel list's "Edit" menu item, via ChannelSettings' initialOpenConfigEdit)
  // land on this tab with the edit dialog already open, matching the email path above.
  useEffect(() => {
    if (!openEditModalRequest || channelType !== 'message') return;
    setEditSpaceModalOpen(true);
    onOpenEditModalRequestHandled?.();
  }, [openEditModalRequest, channelType, onOpenEditModalRequestHandled]);

  useEffect(() => {
    if (!editModalOpen) {
      setIsDescriptionExpanded(false);
    }
  }, [editModalOpen]);

  useEffect(() => {
    if (!reauthPromptOpen) {
      setShowReauthDetails(false);
    }
  }, [reauthPromptOpen]);

  const resetDescriptionScrollState = useCallback(() => {
    setDescriptionHasVerticalScroll(false);
    setDescriptionScrollbarWidth(0);
  }, []);

  const updateDescriptionScrollState = useCallback(() => {
    const el = descriptionTextareaRef.current;
    if (!el) return;
    const hasScroll = el.scrollHeight > el.clientHeight + 1;
    const scrollbarWidth = measureScrollbarWidth(el, hasScroll);
    setDescriptionHasVerticalScroll(hasScroll);
    setDescriptionScrollbarWidth(scrollbarWidth);
  }, []);

  useEffect(() => {
    if (!editModalOpen || !showDescriptionField || showDescriptionCards) {
      resetDescriptionScrollState();
      return;
    }

    const frame = requestAnimationFrame(updateDescriptionScrollState);
    return () => cancelAnimationFrame(frame);
  }, [
    description,
    editModalOpen,
    showDescriptionField,
    showDescriptionCards,
    resetDescriptionScrollState,
    updateDescriptionScrollState,
  ]);

  useEffect(() => {
    if (!editModalOpen || !showDescriptionField || showDescriptionCards) {
      resetDescriptionScrollState();
      return;
    }

    let observer: ResizeObserver | null = null;
    const frame = requestAnimationFrame(() => {
      const el = descriptionTextareaRef.current;
      if (!el) return;

      updateDescriptionScrollState();
      observer = new ResizeObserver(updateDescriptionScrollState);
      observer.observe(el);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [
    editModalOpen,
    showDescriptionField,
    showDescriptionCards,
    resetDescriptionScrollState,
    updateDescriptionScrollState,
  ]);

  // Refresh Last Sync / Next Sync relative strings every 60s (align with threads page header behavior)
  const [syncTimeTick, setSyncTimeTick] = useState(0);
  useEffect(() => {
    if (!emailSyncData?.email_sync || channelType !== 'email') return;
    const interval = setInterval(() => setSyncTimeTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, [emailSyncData?.email_sync, channelType]);

  // Handle disconnect connection (only for email channels)
  const handleDisconnect = async () => {
    if (!channelName || channelType !== 'email') return;
    
    try {
      setIsDisconnecting(true);
      
      const token = localStorage.getItem('refreshToken') || '';
      const response = await disconnectEmailAccount(channelName, token);
      
      if (response.success) {
        // Emit socket event for email disconnected
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.EMAIL_DISCONNECTED,
              data: {
                emailId: userData.email_id,
                channelId: channelId
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Email connection disconnected successfully');
        setDisconnectDialogOpen(false);
        // Optimistic update so Connection Status and Sync Status update immediately in the UI
        setEmailSyncData(prev => prev && prev.email_sync
          ? { ...prev, email_sync: { ...prev.email_sync, is_active: false } }
          : prev);
        // Refetch after short delay so backend state is reflected when ready
        setTimeout(() => refreshEmailSyncStatus(channelName, token, channelType), 800);
      } else {
        showError(response.message || 'Failed to disconnect email connection');
      }
    } catch (error) {
      console.error('Error disconnecting email:', error);
      showError(error instanceof Error ? error.message : 'Failed to disconnect email connection');
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Handle reconnect connection (only for email channels)
  const handleReconnect = async () => {
    if (!channelName || channelType !== 'email') return;
    
    try {
      setIsReconnecting(true);
      
      const token = localStorage.getItem('refreshToken') || '';
      const response = await reconnectEmailAccount(channelName, token);
      
      if (response.success) {
        // Emit socket event for email reconnected
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.EMAIL_RECONNECTED,
              data: {
                emailId: userData.email_id,
                channelId: channelId
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Email connection reconnected successfully');
        // Optimistic update so Connection Status and Sync Status update immediately in the UI
        setEmailSyncData(prev => prev && prev.email_sync
          ? { ...prev, email_sync: { ...prev.email_sync, is_active: true, sync_status: 'pending' } }
          : prev);
        // Refetch after short delay so backend state is reflected when ready (and restart polling)
        setTimeout(() => refreshEmailSyncStatus(channelName, token, channelType), 800);
      } else {
        showError(response.message || 'Failed to reconnect email connection');
      }
    } catch (error) {
      if ((error as ReauthRequiredError)?.requiresReauth) {
        // Stored refresh token was revoked — offer a fresh OAuth consent instead of a bare error toast.
        setReauthMessage((error as ReauthRequiredError).message);
        setReauthProvider((error as ReauthRequiredError).provider);
        setReauthPromptOpen(true);
        return;
      }
      console.error('Error reconnecting email:', error);
      showError(error instanceof Error ? error.message : 'Failed to reconnect email connection');
    } finally {
      setIsReconnecting(false);
    }
  };

  /**
   * "Sign in again" on the reauth prompt — opens the same OAuth popup pattern used by
   * AddSpaceModal's handleGmailOAuth/handleOffice365OAuth, tagging state with
   * email_reauth|<base64url(channelName)> so the callback page can identify this flow and
   * thread the connected email address back through postMessage (§4.1/§4.3 of the FE doc).
   */
  const handleReauthOAuth = () => {
    if (!channelName) return;
    setReauthPopupLoading(true);
    const currentOrigin = window.location.origin;
    const encodedEmail = btoa(channelName).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const state = `email_reauth|${encodedEmail}`;

    let authUrl: string;
    if (reauthProvider === 'microsoft') {
      const redirectUri = `${currentOrigin}/auth/office365/callback`;
      const scope = 'https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/User.Read openid offline_access';
      authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${OFFICE365_CLIENT_ID}&` +
        `response_type=code&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_mode=query&` +
        `state=${encodeURIComponent(state)}&` +
        `prompt=select_account`;
    } else {
      const redirectUri = `${currentOrigin}/auth/google/callback`;
      const scope =
        'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid';
      authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GMAIL_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        // access_type=offline + prompt=consent are required so the provider actually returns a
        // refresh_token — see the FE doc's §2.3 error table entry for the 400 backend raises
        // otherwise ("did not return a refresh token").
        `access_type=offline&` +
        `prompt=${encodeURIComponent('select_account consent')}&` +
        `state=${encodeURIComponent(state)}`;
    }

    const popup = window.open(authUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');

    if (reauthPopupIntervalRef.current) {
      clearInterval(reauthPopupIntervalRef.current);
    }
    reauthPopupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(reauthPopupIntervalRef.current!);
        reauthPopupIntervalRef.current = null;
        setReauthPopupLoading(false);
      }
    }, 1000);
  };

  // Completes the reauth flow once the popup delivers fresh tokens via postMessage:
  // POST them to /reauthorize (access_token/refresh_token path — see FE doc §4, not oauth_code,
  // since that backend path is unverified against live OAuth per the backend PR notes).
  const completeReauth = useCallback(async (emailAddress: string, tokenData: {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    provider?: string;
  }) => {
    try {
      const token = localStorage.getItem('refreshToken') || '';
      const result = await reauthorizeEmailAccount(
        emailAddress,
        {
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          expires_in: tokenData.expires_in,
          provider: reauthProvider || tokenData.provider,
        },
        token
      );

      if (result.success) {
        showSuccess(result.message || 'Email account re-authorized successfully');
        setReauthPromptOpen(false);
        setEmailSyncData(prev => prev && prev.email_sync
          ? { ...prev, email_sync: { ...prev.email_sync, is_active: true, sync_status: 'pending' } }
          : prev);
        setTimeout(() => channelName && refreshEmailSyncStatus(channelName, token, channelType), 800);
      } else {
        showError(result.message || 'Failed to re-authorize email connection');
      }
    } catch (error) {
      // Covers §2.3's error table verbatim — e.g. "You signed in as X, but this channel is
      // connected to Y..." must reach the user as-is, not be swallowed into a generic message.
      showError(error instanceof Error ? error.message : 'Failed to re-authorize email connection');
    } finally {
      setIsReauthorizing(false);
    }
  }, [reauthProvider, channelType, channelName, showSuccess, showError]);

  // postMessage listener for the reauth popup flow. Must check emailReauthAddress BEFORE any
  // generic OAUTH_SUCCESS handling and return early — mirrors the ordering rationale documented
  // at AddSpaceModal.tsx around its google_chat_inbox branch (a differently-tagged OAuth flow
  // must never fall through into a different flow's handling).
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const { type, data } = event.data || {};

      if (type === 'OAUTH_CANCELLED' && data?.emailReauthAddress) {
        setReauthPopupLoading(false);
        if (reauthPopupIntervalRef.current) {
          clearInterval(reauthPopupIntervalRef.current);
          reauthPopupIntervalRef.current = null;
        }
        return;
      }

      if (type === 'OAUTH_SUCCESS' && data?.emailReauthAddress) {
        // Clear the popup-open flag/interval synchronously here — mirrors AddSpaceModal.tsx's
        // OAUTH_SUCCESS handling (clear loading + interval immediately, before any further async
        // work). The popup has already closed itself by the time this message arrives; leaving
        // the interval running would otherwise flip reauthPopupLoading false ~1s later on its own,
        // independent of (and racing) the /reauthorize call started below (PR #594 review).
        setReauthPopupLoading(false);
        if (reauthPopupIntervalRef.current) {
          clearInterval(reauthPopupIntervalRef.current);
          reauthPopupIntervalRef.current = null;
        }
        setIsReauthorizing(true);
        completeReauth(data.emailReauthAddress, {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_in: data.expires_in,
          provider: data.provider,
        });
        return;
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [completeReauth]);

  // Handle delete connection (for email, message, and whatsapp channels)
  const handleDelete = async () => {
    if (!channelId || !channelName) return;
    
    try {
      setIsDeleting(true);
      
      const token = localStorage.getItem('refreshToken') || '';
      
      // For email channels, delete email data using email cleanup API
      // For message/whatsapp channels, delete the channel directly
      if (channelType === 'email') {
        const response = await deleteEmailData(channelName, token);
        
        if (response.success) {
          // Emit socket event for channel deleted
          const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          if (loggedInUserData) {
            try {
              const userData = JSON.parse(loggedInUserData);
              const eventData = {
                type: SOCKET_KEYS.CHANNEL_DELETED,
                data: {
                  emailId: userData.email_id,
                  channelId: channelId
                }
              };
              
              await socketService.sendSocketMessage(eventData);
            } catch (error) {
              // Handle error silently
            }
          }
          
          showSuccess('Email deleted successfully');
          setDeleteDialogOpen(false);
          // Close channel settings dialog before navigating
          if (onClose) {
            onClose();
          }
          // Redirect to supportSpace screen after successful deletion
          navigate('/supportSpace');
        } else {
          showError(response.message || 'Failed to delete email data');
        }
      } else {
        // For message/whatsapp channels, delete the channel using the channel API
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.DELETE(channelId)}`,
          {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to delete channel: ${response.status}`);
        }

        // Emit socket event for channel deleted
        const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (loggedInUserData) {
          try {
            const userData = JSON.parse(loggedInUserData);
            const eventData = {
              type: SOCKET_KEYS.CHANNEL_DELETED,
              data: {
                emailId: userData.email_id,
                channelId: channelId
              }
            };
            
            await socketService.sendSocketMessage(eventData);
          } catch (error) {
            // Handle error silently
          }
        }
        
        showSuccess('Channel deleted successfully');
        setDeleteDialogOpen(false);
        // Close channel settings dialog before navigating
        if (onClose) {
          onClose();
        }
        // Redirect to supportSpace screen after successful deletion
        navigate('/supportSpace');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage = channelType === 'email' 
        ? 'Failed to delete email data'
        : 'Failed to delete channel';
      showError(error instanceof Error ? error.message : errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Get connection status display info
  const getConnectionStatusInfo = () => {
    if (!emailSyncData?.email_sync) {
      return {
        status: 'Not Connected',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />
      };
    }

    const { is_active } = emailSyncData.email_sync;
    
    if (is_active) {
      return {
        status: 'Connected',
        color: 'success' as 'error' | 'success' | 'warning',
        icon: <CheckCircleIcon />
      };
    } else {
      return {
        status: 'Disconnected',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />
      };
    }
  };

  // Get sync status display info
  const getSyncStatusInfo = () => {
    if (!emailSyncData?.email_sync) {
      return {
        status: 'Not Synced',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />
      };
    }

    const { sync_status, is_active } = emailSyncData.email_sync;
    
    // If connection is disconnected, show appropriate status
    if (!is_active) {
      return {
        status: 'Connection Lost',
        color: 'error' as 'error' | 'success' | 'warning',
        icon: <ErrorIcon />
      };
    }
    
    if (sync_status === 'success') {
      return {
        status: 'Sync Completed',
        color: 'success' as 'error' | 'success' | 'warning',
        icon: <CheckCircleIcon />
      };
    }
    // API may return "pending" or "in_progress" while syncing
    if (sync_status === 'pending' || sync_status === 'in_progress') {
      return {
        status: 'Syncing',
        color: 'warning' as 'error' | 'success' | 'warning',
        icon: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <SyncIcon sx={{ fontSize: 20 }} />
          </Box>
        )
      };
    }
    return {
      status: 'Sync Failed',
      color: 'error' as 'error' | 'success' | 'warning',
      icon: <ErrorIcon />
    };
  };

  const connectionStatusInfo = getConnectionStatusInfo();
  const syncStatusInfo = getSyncStatusInfo();

  // Get email ID from sync data
  const getEmailId = () => {
    return emailSyncData?.email_address || channelName || '';
  };

  // Create stable references for arrays to avoid dependency array issues (matching AddSpaceModal)
  const stableCategories = useMemo(() => JSON.stringify(selectedCategories), [selectedCategories]);
  const stableKeywords = useMemo(() => JSON.stringify(flatSelectedKeywords), [flatSelectedKeywords]);
  const stableDescription = useMemo(() => description || '', [description]);

  // Fetch description suggestions when the admin edits tags/subtags, or explicitly clicks
  // "View Suggestions" (descriptionFetchNonce) — but NOT merely from opening the dialog with
  // its already-saved tags. previousSelectedCategoriesRef/previousSelectedKeywordsRef are
  // seeded to the current tags on open (openChannelEditModal), so categoriesChanged/
  // keywordsChanged below are false right after open and only turn true on a real edit.
  useEffect(() => {
    // Decide synchronously — before the 500ms debounce delay — whether this render represents
    // a genuine new edit (tag/subtag change, explicit "View Suggestions" click, or the dialog
    // closing) that invalidates anything currently in flight or still debouncing. PR #604
    // review (High): the request id previously only incremented once the debounced
    // fetchDescriptions callback actually ran, so an older in-flight request stayed "latest"
    // for the full 500ms window after a newer edit — its response could overwrite suggestions
    // meant for the newer tag selection, or land after the dialog had already closed. Bumping
    // here, synchronously on every render this effect fires for, closes that window:
    // descriptionRequestIdRef.current advances the instant a new edit (or a close) is
    // detected, regardless of whether this render's own debounced fetch has run yet.
    const nonceAdvanced = descriptionFetchNonce !== handledDescriptionFetchNonceRef.current;
    const categoriesChanged = JSON.stringify(previousSelectedCategoriesRef.current) !== stableCategories;
    const keywordsChanged = JSON.stringify(previousSelectedKeywordsRef.current) !== stableKeywords;
    const invalidatesInFlight = categoriesChanged || keywordsChanged || nonceAdvanced || !editModalOpen;
    const myRequestId = invalidatesInFlight ? ++descriptionRequestIdRef.current : descriptionRequestIdRef.current;

    const fetchDescriptions = async () => {
      handledDescriptionFetchNonceRef.current = descriptionFetchNonce;

      // Only fetch if modal is open and we have required data
      // Categories are required for description generation (matching AddSpaceModal behavior)
      if (!editModalOpen || !workspaceTag || !getEmailId()) {
        return;
      }

      // Parse stable categories and keywords to check we have both tag and subtag
      const categoriesArray = stableCategories ? JSON.parse(stableCategories) : [];
      const keywordsArray = stableKeywords ? JSON.parse(stableKeywords) : [];
      if (!categoriesArray || categoriesArray.length === 0) {
        // Reset when conditions are not met (but only if we don't have a description already)
        if (!description.trim()) {
          setDescriptionSuggestions([]);
          setShowDescriptionCards(false);
          setShowDescriptionField(false);
        }
        setLoadingDescriptions(false);
        return;
      }
      // Require at least one subtag before calling description API (workspace + tag + subtag)
      if (!keywordsArray || keywordsArray.length === 0) {
        if (!description.trim()) {
          setDescriptionSuggestions([]);
          setShowDescriptionCards(false);
          setShowDescriptionField(false);
        }
        setLoadingDescriptions(false);
        return;
      }

      // Refetch when tags/subtags actually changed, or the admin explicitly clicked "View
      // Suggestions" (nonceAdvanced). Deliberately does NOT include a standalone
      // "descriptionSuggestions.length === 0" catch-all — that was what caused this to fire
      // on every dialog open even with no tag change and no click. Reuses the categoriesChanged/
      // keywordsChanged/nonceAdvanced computed above (nothing async has happened in between).
      const shouldRefetch = categoriesChanged || keywordsChanged || nonceAdvanced;

      if (!shouldRefetch) {
        return;
      }

      // Deliberately does NOT abort a still-in-flight request here. generate-description is a
      // slow ML endpoint (tens of seconds); the previous code aborted any in-flight call the
      // instant another tag/subtag was edited (both here AND in this effect's cleanup below),
      // so adding several tags/subtags in one sitting reliably killed the request before it
      // could ever finish — visible in DevTools as repeated failed "generate-description"
      // calls. Correctness instead comes from myRequestId, reserved synchronously above before
      // this debounce fired — only a response matching the CURRENT descriptionRequestIdRef is
      // ever applied to state.
      const abortController = new AbortController();
      descriptionAbortControllerRef.current = abortController;

      const currentRequestId = myRequestId;
      const hasUserSelectedDescription = description.trim() !== '';

      // Update refs to track current values (matching AddSpaceModal)
      previousSelectedCategoriesRef.current = [...selectedCategories];
      previousSelectedKeywordsRef.current = [...flatSelectedKeywords];

      // Set loading state first to maintain consistent UI height (matching AddSpaceModal)
      // Don't clear suggestions immediately to prevent flicker
      setLoadingDescriptions(true);
      
      // Show the description section immediately when loading starts. When categories/keywords change,
      // always show the suggestion view.
      if (categoriesChanged || keywordsChanged) {
        setShowDescriptionField(false);
        setShowDescriptionCards(true);
      } else if (!hasUserSelectedDescription && !showDescriptionField && !showDescriptionCards) {
        setShowDescriptionCards(true);
      }
      
      // Keep previous suggestions visible during loading to prevent height changes
      // Only clear if we're starting fresh (no previous suggestions)
      if (descriptionSuggestions.length === 0) {
        // Only clear if we don't have any suggestions yet
      }
      
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        if (!token) {
          setLoadingDescriptions(false);
          return;
        }

        // Parse stable keywords for the request
        const keywordsArray = stableKeywords ? JSON.parse(stableKeywords) : [];

        // Build request payload with categories and tags (matching AddSpaceModal)
        const requestPayload = {
          email_id: getEmailId(),
          workspace_name: workspaceTag,
          categories: categoriesArray, // Categories are required
          tags: keywordsArray, // Keywords (sub tags) can be empty array
        };

        const response = await generateDescription(
          requestPayload,
          token,
          abortController.signal
        );

        // Check if this request is still the latest one and wasn't aborted
        if (currentRequestId === descriptionRequestIdRef.current && !abortController.signal.aborted) {
          if (response.status === 'success' && response.descriptions) {
            // Only update suggestions if user hasn't selected a description,
            // or if the selected description is no longer in the new suggestions
            // Matching AddSpaceModal behavior
            const shouldUpdateSuggestions = !hasUserSelectedDescription || 
              !response.descriptions.some(desc => desc.trim() === stableDescription.trim());
            
            if (shouldUpdateSuggestions) {
              // Update suggestions and loading state together to prevent flicker
              setDescriptionSuggestions(response.descriptions);
              setLoadingDescriptions(false);
              
              // Only auto-show cards if user hasn't selected a description
              if (!hasUserSelectedDescription) {
                setShowDescriptionCards(true);
                setShowDescriptionField(false);
              }
            } else {
              // User has selected a description that's still valid, just update suggestions silently
              setDescriptionSuggestions(response.descriptions);
              setLoadingDescriptions(false);
            }
          } else {
            // No descriptions returned, stop loading
            setLoadingDescriptions(false);
          }
        }
      } catch (error: any) {
        // Don't show error if request was aborted (cancelled)
        if (error.name === 'AbortError' || abortController.signal.aborted) {
          // Silently ignore aborted requests - this is expected behavior
          return;
        }
        console.error('Error fetching descriptions:', error);
        // Don't show error to user, just silently fail
        if (currentRequestId === descriptionRequestIdRef.current) {
          setDescriptionSuggestions([]);
        }
      } finally {
        // Only update loading state if this is still the latest request
        // Matching AddSpaceModal behavior
        if (currentRequestId === descriptionRequestIdRef.current) {
          setLoadingDescriptions(false);
        }
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchDescriptions, 500);
    return () => {
      // Only cancels the pending (not-yet-fired) debounced call on a rapid follow-up edit —
      // does NOT abort a request that has already started (see the comment above
      // fetchDescriptions's abortController for why an in-flight call is now left to finish).
      clearTimeout(timeoutId);
    };
  }, [descriptionFetchNonce, editModalOpen, workspaceTag, stableCategories, stableKeywords, channelName, stableDescription, showDescriptionField, showDescriptionCards, descriptionSuggestions.length]);

  // Use shared hook for description handlers (matching AddSpaceModal)
  const {
    handleDescriptionSelect,
    handleDescriptionEdit,
  } = useDescriptionHandlers({
    onDescriptionChange: (newDescription: string) => {
      setDescription(newDescription);
      // The admin just finalized a new description from suggestions — refresh business
      // rules suggestions to match it. Tags/subtags edits alone must never do this (see
      // BusinessRulesSection's descriptionSelectionNonce prop).
      setBusinessRulesFetchNonce((n) => n + 1);
    },
    showDescriptionField,
    setShowDescriptionField,
    setShowDescriptionCards,
    disabled: false, // Allow toggling in edit modal
  });

  // Custom toggle handler matching AddSpaceModal behavior
  const handleToggleDescriptionView = () => {
    if (showDescriptionField) {
      // Switch to cards view - show suggestions
      setShowDescriptionField(false);
      setShowDescriptionCards(true);
      // Explicit "View Suggestions" click — the only thing allowed to trigger the
      // description-suggestions API call in this dialog (see descriptionFetchNonce above).
      if (descriptionSuggestions.length === 0 && !loadingDescriptions) {
        setDescriptionFetchNonce((n) => n + 1);
      }
    } else {
      // Switch to input field view
      setShowDescriptionField(true);
      setShowDescriptionCards(false);
    }
  };

  // Fetch updated channel data
  const fetchUpdatedChannelData = async () => {
    if (!channelId) return;

    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(channelId)}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update local state with the fetched channel data (including tags/subtags like channel list)
      if (data.name) {
        setCurrentChannelName(data.name);
      }
      if (data.channel_description !== undefined) {
        setCurrentDescription(data.channel_description || '');
      } else if (data.description !== undefined) {
        setCurrentDescription(data.description || '');
      }
      if (data.channel_tags !== undefined) {
        setCurrentChannelTags(Array.isArray(data.channel_tags) ? data.channel_tags : null);
      }
      if (data.channel_sub_tags !== undefined) {
        setCurrentChannelSubtags(normalizeSubtags(data.channel_sub_tags));
        setCurrentChannelSubtagsRaw(data.channel_sub_tags ?? null);
      }
      if (data.channel_business_rules !== undefined) {
        setCurrentBusinessRules(data.channel_business_rules || '');
      }
      if (data.channel_settings?.spam_auto_push_enabled !== undefined || data.spam_auto_push_enabled !== undefined) {
        const spamAutoPushValue = !!(data.channel_settings?.spam_auto_push_enabled ?? data.spam_auto_push_enabled);
        setSpamAutoPushEnabled(spamAutoPushValue);
        if (channelId) spamAutoPushCache.set(channelId, spamAutoPushValue);
      }
    } catch (error) {
      console.error('Error fetching updated channel data:', error);
      // Don't show error to user, just log it
      // The socket event should eventually update the parent component
    }
  };

  // spam_auto_push_enabled — true persistence: this reflects the real, saved backend value every
  // time Channel Config is opened (tab-switch away-and-back, or closing/reopening the whole
  // Settings panel), not just the current session. Reads both a nested `channel_settings.
  // spam_auto_push_enabled` and a top-level `spam_auto_push_enabled` on the GET response, in case
  // the read shape ever differs from the nested shape the PUT below writes — defaults to false
  // (Enable) whenever neither is present, per the API's own documented default.
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const response = await fetch(
          `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(channelId)}`,
          {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) {
          const spamAutoPushValue = !!(data?.channel_settings?.spam_auto_push_enabled ?? data?.spam_auto_push_enabled);
          setSpamAutoPushEnabled(spamAutoPushValue);
          spamAutoPushCache.set(channelId, spamAutoPushValue);
        }
      } catch (error) {
        console.error('Error fetching spam_auto_push_enabled state:', error);
      }
    })();

    return () => { cancelled = true; };
  }, [channelId]);

  // Toggle spam_auto_push_enabled — sends the opposite of the current value, updating local
  // state only after the server confirms the change (never optimistic), so a failed request
  // never leaves the button showing a state the backend didn't actually persist.
  const handleToggleSpamAutoPush = async () => {
    if (!channelId) return;
    const nextValue = !spamAutoPushEnabled;

    try {
      setIsTogglingSpamAutoPush(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

      // PR #658 review [M1] (2nd round): the prior GET-then-merge-then-PUT here was based on an
      // incorrect premise. shay-suite-backend's PUT /channels/{id} already reads channel_settings
      // fresh from the DB and merges it server-side (merged_settings = {**existing_settings,
      // **validated_dump}) — it never replaces the JSONB column wholesale, and unset
      // ChannelSettingsUpdate fields are dropped via exclude_none=True before that merge runs. A
      // genuinely partial PUT is therefore already merge-safe. The client-side GET this replaced
      // was strictly worse: sending back the whole fetched channel_settings object made every one
      // of those fields "explicitly set by this request," so a concurrent edit to e.g.
      // auto_draft_enabled landing in the GET-to-PUT window would get silently reverted by this
      // toggle — the same class of bug the GET was meant to prevent, just narrower.
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          channel_settings: {
            spam_auto_push_enabled: nextValue
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSpamAutoPushEnabled(nextValue);
      spamAutoPushCache.set(channelId, nextValue);
      showSuccess(nextValue ? 'Spam auto-push enabled.' : 'Spam auto-push disabled.');
    } catch (error) {
      console.error('Error toggling spam_auto_push_enabled:', error);
      showError('Failed to update spam auto-push. Please try again.');
    } finally {
      setIsTogglingSpamAutoPush(false);
    }
  };

  // Handle update channel
  const handleUpdateChannel = async () => {
    if (!channelId || !description.trim()) {
      showError('Description is required');
      return;
    }

    if (!channelName || !channelName.trim()) {
      showError('Channel name is required');
      return;
    }

    if (
      channelType === 'email' &&
      editBusinessRules.trim() &&
      (!businessRulesCanUpdate || businessRulesValidationStatus === 'pending' || businessRulesValidationStatus === 'validating')
    ) {
      showError('Validate business rules before updating the channel.');
      return;
    }

    try {
      setIsUpdating(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      // Update channel with name, description, channel_tags and channel_sub_tags (same as channel_tags for edit)
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          name: channelName.trim(),
          channel_description: description.trim(),
          ...(selectedCategories.length > 0 ? { channel_tags: selectedCategories } : {}),
          // Per-tag subtags dictionary: { "Customer Support": [...], "HR": [...] }
          // W-2: Backend endpoints (channel create + update) must accept Record<string,string[]> for channel_sub_tags.
          ...(Object.keys(selectedKeywordsPerTag).length > 0 ? { channel_sub_tags: selectedKeywordsPerTag } : {}),
          ...(editBusinessRules.trim() ? { channel_business_rules: editBusinessRules.trim() } : {}),
          ...(editBusinessRules.trim() && businessRulesValidationResult
            ? { validation_result: businessRulesValidationResult }
            : {}),
          ...(editBusinessRules.trim() && businessRulesForceSave ? { force_save: true } : {}),
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const serverResult = (errorData?.validation_result ||
          errorData?.detail?.validation_result) as BusinessRulesValidationResult | undefined;
        if (response.status === 422 && serverResult) {
          setServerValidationResult(serverResult);
          setBusinessRulesValidationResult(serverResult);
          setBusinessRulesValidationStatus(serverResult.status);
          setBusinessRulesCanUpdate(serverResult.status === 'clean' || serverResult.status === 'warnings');
          setBusinessRulesForceSave(serverResult.status === 'warnings');
          showError('Business rules validation failed. Please review and try again.');
          return;
        }
        throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
      }

      // Emit socket event for channel updated
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        try {
          const userData = JSON.parse(loggedInUserData);
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_UPDATED,
            data: {
              emailId: userData.email_id,
              channelId: channelId
            }
          };
          
          await socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }

      // Update local state with new description and tags/subtags so UI reflects change before parent refetches
      setCurrentDescription(description.trim());
      if (editBusinessRules.trim()) {
        setCurrentBusinessRules(editBusinessRules.trim());
      }
      setCurrentChannelTags(selectedCategories);
      setCurrentChannelSubtags(flatSelectedKeywords);
      setCurrentChannelSubtagsRaw(selectedKeywordsPerTag);
      
      showSuccess('Channel updated successfully');
      // Close the edit modal dialog
      setEditModalOpen(false);

      // Fix: tell the parent to do a clean API re-fetch of channel details now, not just rely
      // on this component's local optimistic state — that local state resets to the (now
      // stale) incoming props the moment this tab remounts (tab switch or Settings drawer
      // close+reopen), which was causing the edit to visually "revert" on navigation back.
      onChannelUpdated?.();
    } catch (error) {
      console.error('Error updating channel:', error);
      showError(error instanceof Error ? error.message : 'Failed to update channel');
    } finally {
      setIsUpdating(false);
    }
  };

  // Trigger topic/classification training (retrain) via train-wrapper API
  const handleRetrainTraining = async (fullRetrain: boolean) => {
    if (!channelId) {
      showError('Channel ID is required');
      return;
    }
    try {
      setRetrainLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const payload = {
        channelId,
        channelDescription: currentDescription || '',
        mainCategories: displayChannelTags,
        full_retrain: fullRetrain,
      };
      const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.TOPIC_TRAIN_WRAPPER}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = typeof errorData?.detail === 'string' ? errorData.detail : errorData?.message || `Request failed: ${response.status}`;
        throw new Error(message);
      }
      const result = await response.json().catch(() => ({}));
      setRetrainDialogOpen(false);
      const isStarted = result?.status === 'started';
      if (isStarted) {
        showSuccess(result?.message || 'Topic model training has been started for this channel.');
        // Optimistically set training in progress so UI shows "Training in progress" and hides time/retrain
        setEmailSyncData((prev) =>
          prev && prev.topic_training
            ? { ...prev, topic_training: { ...prev.topic_training, training_inprogress: true } }
            : prev ? { ...prev, topic_training: { training_inprogress: true } } : prev
        );
        // Notify threads page so classification panel shows "Processing your inbox..." without refresh
        if (channelId && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('channel-classification-training-started', { detail: { channelId } }));
        }
      } else {
        showSuccess(fullRetrain ? 'Classification training started from scratch.' : 'Retrain requested.');
      }
      // Refresh sync status so last/next topic training and training_inprogress stay in sync
      if (channelName) refreshEmailSyncStatus(channelName, token, channelType);
    } catch (error) {
      console.error('Retrain request error:', error);
      showError(error instanceof Error ? error.message : 'Failed to start training');
    } finally {
      setRetrainLoading(false);
    }
  };

  // If not an email channel, show message/voice channel config
  if (channelType !== 'email') {
    // Create a minimal channel object for EditSpaceModal (only for message channels)
    const channelForEdit = channelId && currentChannelName ? {
      id: channelId,
      name: currentChannelName,
      description: currentDescription || '',
      channel_business_rules: currentBusinessRules || '',
      workspace_id: '',
      workspace_name: workspaceTag || '',
      company_id: '',
      is_active: true,
      is_public: false,
      ai_enabled: false,
      channel_settings: {
        auto_summarize: false,
        notification_preferences: {
          email: false,
          push: false,
        },
      },
      message_count: 0,
      last_message_at: null,
      total_apps_connected: 0,
      total_users: 0,
      total_datasources: 0,
      app: [],
      datasource: [],
      agent_count: 0,
      agent_names: {},
      workflow_count: 0,
      RCA: '',
      created_at: '',
      updated_at: '',
    } : null;

    return (
      <Box sx={{ p: 2 }}>
        <Card>
          <CardContent>
            {/* Channel name with edit and delete buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                {currentChannelName || 'Channel Configuration'}
              </Typography>
              {isChannelAdmin && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Edit button - only show for message channels */}
                  {channelType === 'message' && channelId && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={() => setEditSpaceModalOpen(true)}
                      disabled={isChannelArchived}
                      sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  {/* Delete button - show for message channels (includes whatsapp, slack, etc.) */}
                  {channelType === 'message' && channelId && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={isChannelArchived}
                    >
                      Delete Channel
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Purpose of the channel - show for message channels (channel tags are email-only, not shown here) */}
            {channelType === 'message' && currentDescription && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Purpose of the channel
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {currentDescription}
                </Typography>
              </Box>
            )}

            {/* Business rules - show for message channels when present */}
            {channelType === 'message' && currentBusinessRules && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Business Rules
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {currentBusinessRules}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Edit Space Modal - reused from GroupsPage for message channels */}
        {channelType === 'message' && (
          <EditSpaceModal
            open={editSpaceModalOpen}
            onClose={() => setEditSpaceModalOpen(false)}
            channel={channelForEdit}
            channelType="message"
            isChannelAdmin={isChannelAdmin}
            onBuildWorkflow={onBuildWorkflow}
            onSpaceUpdated={async () => {
              // Fetch the updated channel data to refresh local state
              await fetchUpdatedChannelData();
              // Show success message
              showSuccess(`${LanguageConstants.topic} updated successfully`);
              // Close the modal
              setEditSpaceModalOpen(false);
              // Fix: same stale-parent-prop issue as the email edit path — refresh the
              // parent's real channelData too, so the edit survives a tab switch or
              // Settings drawer close+reopen for message channels as well.
              onChannelUpdated?.();
            }}
          />
        )}

        {/* Delete Confirmation Dialog - for message channels (includes whatsapp, slack, etc.) */}
        {channelType === 'message' && (
          <Dialog
            open={deleteDialogOpen}
            onClose={(event, reason) => {
              if (reason === "backdropClick") {
                return; // Prevent closing when clicking outside
              }
              setDeleteDialogOpen(false);
            }}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            sx={{
              zIndex: 9999,
              '& .MuiDialog-paper': {
                backgroundColor: theme.palette.background.paper,
                backgroundImage: 'none',
                color: theme.palette.text.primary,
              },
            }}
          >
            <DialogTitle sx={{ color: 'text.primary' }}>
              Delete Channel
            </DialogTitle>
            <DialogContent sx={{ color: 'text.secondary' }}>
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ color: 'text.primary' }}>
                  {`Are you sure you want to delete the channel "${currentChannelName || channelName}"?`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This action will permanently delete the channel and all associated data. This cannot be undone.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Email Sync Status Section - Only for email channels */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon />
                {channelName || 'No email connected'}
              </Typography>
            </Box>
            {isChannelAdmin && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {/* Edit button - only show for email and whatsapp channels */}
                {(channelType === 'email' || channelType === 'message') && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={openChannelEditModal}
                    disabled={isChannelArchived}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }}
                  >
                    Edit
                  </Button>
                )}
                {/* Show Reconnect button when is_active is false */}
                {!emailSyncData?.email_sync?.is_active && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleReconnect}
                    disabled={isChannelArchived || isReconnecting}
                  >
                    {isReconnecting ? 'Reconnecting...' : 'Reconnect'}
                  </Button>
                )}
                {/* Spam Auto Push toggle moved to the "Last classification training" slot
                    below, per request — see that Box further down in this file. */}
                {/* Only show Disconnect button when is_active is true */}
                {emailSyncData?.email_sync?.is_active && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DisconnectIcon />}
                    onClick={() => setDisconnectDialogOpen(true)}
                    disabled={isChannelArchived}
                  >
                    Disconnect
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={!emailSyncData?.email_sync || isChannelArchived}
                >
                  Delete Connection
                </Button>
              </Box>
            )}
          </Box>

          {emailSyncError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {emailSyncError}
            </Alert>
          )}

          {/* All Status Items in Single Line */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Connection Status */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Connection status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {initialLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    icon={connectionStatusInfo.icon}
                    label={connectionStatusInfo.status}
                    color={connectionStatusInfo.color}
                    size="medium"
                    variant="filled"
                    sx={{
                      backgroundColor: (() => {
                        switch (connectionStatusInfo.color) {
                          case 'success': return theme.palette.success.main;
                          case 'warning': return theme.palette.warning.main;
                          case 'error': return theme.palette.error.main;
                          default: return theme.palette.grey[500];
                        }
                      })(),
                      color: 'white',
                      fontWeight: 'medium',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Sync Status */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sync status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {initialLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    icon={syncStatusInfo.icon}
                    label={syncStatusInfo.status}
                    color={syncStatusInfo.color}
                    size="medium"
                    variant="filled"
                    sx={{
                      backgroundColor: (() => {
                        switch (syncStatusInfo.color) {
                          case 'success': return theme.palette.success.main;
                          case 'warning': return theme.palette.warning.main;
                          case 'error': return theme.palette.error.main;
                          default: return theme.palette.grey[500];
                        }
                      })(),
                      color: 'white',
                      fontWeight: 'medium',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Sync started on - placed before Last sync per layout */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sync started on
              </Typography>
              <Tooltip title={emailSyncData?.app_account?.created_at ? String(parseUTCToLocal(emailSyncData.app_account.created_at)) : 'Never'}>
                <Typography variant="body2" sx={{ cursor: 'help' }}>
                  {emailSyncData ? String(formatRelativeTime(emailSyncData.app_account.created_at)) : 'Never'}
                </Typography>
              </Tooltip>
            </Box>

            {/* Last Sync */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Last sync
              </Typography>
              <Tooltip title={emailSyncData?.email_sync?.last_sync ? String(parseUTCToLocal(emailSyncData.email_sync.last_sync)) : 'Never'}>
                <Typography variant="body2" sx={{ cursor: 'help' }}>
                  {emailSyncData?.email_sync?.last_sync ? String(formatRelativeTime(emailSyncData.email_sync.last_sync)) : 'Never'}
                </Typography>
              </Tooltip>
            </Box>

            {/* Next Sync - scheduled time for next sync (use direction-aware formatter for future "in X" / past "X ago") */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Next sync
              </Typography>
              <Tooltip title={emailSyncData?.email_sync?.next_sync ? String(parseUTCToLocal(emailSyncData.email_sync.next_sync)) : '—'}>
                <Typography variant="body2" sx={{ cursor: 'help' }}>
                  {emailSyncData?.email_sync?.next_sync
                    ? String(formatSyncTimeWithDirection(emailSyncData.email_sync.next_sync).relative)
                    : '—'}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          {/* Channel tags & subtags - combined so each tag sits directly next to its own subtags */}
          {channelType === 'email' && (
            <Box sx={{ mt: 4, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Channel tags & subtags - width matches Connection status + Sync status + Sync started on (first 3 columns) */}
              <Box sx={{ flex: '4 1 480px', minWidth: 260 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Channel tags &amp; subtags
                </Typography>
                {subtagsGroupedByTag.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {subtagsGroupedByTag.map(({ tag, subtags }) => (
                      <Box
                        key={tag}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          p: 0.75,
                          pl: 1,
                          borderLeft: `3px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}`,
                          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                          borderRadius: '4px',
                        })}
                      >
                        <Tooltip title="Tag these sub tags belong to" arrow placement="top">
                          <Chip
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={(theme) => ({
                              borderRadius: '4px',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              backgroundColor: 'transparent',
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                            })}
                          />
                        </Tooltip>
                        {subtags.length > 0 ? (
                          subtags.map((subtag) => (
                            <Chip
                              key={subtag}
                              label={`#${subtag}`}
                              size="small"
                              variant="outlined"
                              sx={(theme) => ({
                                borderRadius: '4px',
                                fontWeight: 500,
                                fontSize: '0.8125rem',
                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e0e0e0',
                                color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
                                border: 'none',
                              })}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            No sub tags
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : displayChannelTags.length > 0 ? (
                  // Legacy flat format: no per-tag association available - show tags then the flat subtags list, kept close together
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {displayChannelTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={(theme) => ({
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: 'transparent',
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                          })}
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {displayChannelSubtags.length > 0 ? (
                        displayChannelSubtags.map((subtag) => (
                          <Chip
                            key={subtag}
                            label={`#${subtag}`}
                            size="small"
                            variant="outlined"
                            sx={(theme) => ({
                              borderRadius: '4px',
                              fontWeight: 500,
                              fontSize: '0.8125rem',
                              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e0e0e0',
                              color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
                              border: 'none',
                            })}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No sub tags</Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </Box>
              {/* Spam automation toggle — switched from a Button to a Switch to match the
                  approved design (label + info icon on one line, Switch + Enabled/Disabled state
                  text below), relocated here from the header button row into the slot "Last
                  classification training" used to occupy. That content is hidden (commented, not
                  deleted) directly below. */}
              <Box sx={{ flex: 1, minWidth: 225 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Spam automation
                  </Typography>
                  <Tooltip title="When enabled, Prism7 moves emails it classifies as spam to your Gmail Spam or Outlook Junk folder automatically.">
                    <SpamAutoPushInfoIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Switch
                    size="small"
                    checked={spamAutoPushEnabled}
                    onChange={handleToggleSpamAutoPush}
                    disabled={isChannelArchived || isTogglingSpamAutoPush}
                    // MUI v7: Switch always injects its own slotProps.input (role: 'switch') when
                    // forwarding to SwitchBase, which overrides the legacy inputProps prop entirely
                    // — inputProps here would be silently dropped, leaving the input with no
                    // accessible name. slotProps.input is the only prop that actually reaches it,
                    // but it REPLACES (not merges with) MUI's default { role: 'switch' }, so role
                    // must be repeated here too or the input reverts to the implicit checkbox role.
                    slotProps={{ input: { 'aria-label': 'Spam automation', role: 'switch' } }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {isTogglingSpamAutoPush ? 'Updating...' : spamAutoPushEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Box>
              {/* Last classification training — hidden per request (the Spam Auto Push toggle
                  above now occupies this slot); content commented out, not deleted.
              <Box sx={{ flex: 1, minWidth: 225 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ whiteSpace: 'nowrap' }}>
                  Last classification training
                </Typography>
                <Tooltip title={emailSyncData?.topic_training?.last_topic_training ? String(parseUTCToLocal(emailSyncData.topic_training.last_topic_training)) : '-'}>
                  <Typography variant="body2" sx={{ cursor: 'help' }}>
                    {emailSyncData?.topic_training?.last_topic_training ? String(formatRelativeTime(emailSyncData.topic_training.last_topic_training)) : '-'}
                  </Typography>
                </Tooltip>
              </Box>
              */}
              {/* Label Re-classification (renamed from "Next classification training") - show
                  "Training in progress" when training_inprogress, else a Reclassify button (was
                  "Retrain"). The countdown/timing text is hidden per request; commented out
                  below, not deleted. Button's own gating condition (next_topic_training must be
                  truthy) is unchanged. */}
              <Box sx={{ flex: 1, minWidth: 225 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ whiteSpace: 'nowrap' }}>
                  Label Re-classification
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {emailSyncData?.topic_training?.training_inprogress ? (
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                      Training in progress
                    </Typography>
                  ) : (
                    <>
                      {/* Timing text hidden per request; content commented out, not deleted.
                      <Tooltip title={emailSyncData?.topic_training?.next_topic_training ? String(parseUTCToLocal(emailSyncData.topic_training.next_topic_training)) : '—'}>
                        <Typography variant="body2" sx={{ cursor: 'help' }}>
                          {emailSyncData?.topic_training?.next_topic_training
                            ? String(formatSyncTimeWithDirection(emailSyncData.topic_training.next_topic_training).relative)
                            : '—'}
                        </Typography>
                      </Tooltip>
                      */}
                      {emailSyncData?.topic_training?.next_topic_training && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<RefreshIcon sx={{ fontSize: '1rem' }} />}
                          onClick={() => setRetrainDialogOpen(true)}
                          aria-label="Reclassify"
                          sx={{
                            minHeight: 24,
                            py: 0,
                            px: 1,
                            fontSize: '0.75rem',
                            '& .MuiButton-startIcon': { mr: 0.5 },
                          }}
                        >
                          Reclassify
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Purpose of the channel */}
          {currentDescription && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Purpose of the channel
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                {currentDescription}
              </Typography>
            </Box>
          )}

          {channelType === 'email' && channelId && (
            <BusinessRulesSection
              variant="display"
              initialBusinessRules={currentBusinessRules}
            />
          )}

          {/* Progress Bar - show when sync is in progress (API may return "pending" or "in_progress") */}
          {emailSyncData?.email_sync &&
            (emailSyncData.email_sync.sync_status === 'pending' || emailSyncData.email_sync.sync_status === 'in_progress') && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sync Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: theme.palette.grey[300],
                      borderRadius: 4,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${emailSyncData.email_sync.progress_percentage}%`,
                        height: '100%',
                        backgroundColor: theme.palette.primary.main,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {emailSyncData.email_sync.progress_percentage}%
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Signature & Salutation Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
            <Typography variant="h6">
              Signature & Salutation
            </Typography>
            {isChannelAdmin && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setAddSignatureModalOpen(true)}
                disabled={isChannelArchived}
              >
                ADD
              </Button>
            )}
          </Box>
          <SignatureAndSalutation
            channelId={channelId || ''}
            showManagement={true}
            signatureData={signatureData}
            onSignatureDataChange={setSignatureData}
            hideAddButton={true}
            refreshTrigger={refreshTrigger}
          />
        </CardContent>
      </Card>

      {/* Add Signature & Salutation Modal */}
      <AddSignatureAndSalutationModal
        open={addSignatureModalOpen}
        onClose={() => setAddSignatureModalOpen(false)}
        onSave={(data) => {
          // Add the new signature to the list
          setSignatureData(prev => [...prev, data]);
          // Trigger refresh of the SignatureAndSalutation component
          setRefreshTrigger(prev => prev + 1);
          setAddSignatureModalOpen(false);
        }}
        channelId={channelId || ''}
      />

      {/* Reauthorization Required Dialog — revoked refresh token (see docs/EmailReauth_401_Recovery_FE_Implementation.md) */}
      <Dialog
        open={reauthPromptOpen}
        onClose={() => !reauthPopupLoading && !isReauthorizing && setReauthPromptOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Reconnection Required</DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          {/* mdifying_reconection_ui branch — reauthMessage previously replaced this text
              wholesale, dumping the backend's raw technical error (provider error codes,
              trace/correlation IDs, timestamps — see docs/EmailReauth_401_Recovery_FE_Implementation.md)
              straight into the dialog. This Alert now always leads with the same clean,
              friendly copy used before reauthMessage existed; the raw detail (still shown in
              full, nothing dropped) moves behind a "Show technical details" toggle below,
              matching this file's existing Alert-for-warnings convention (e.g. the business
              rules validation warnings box) instead of a plain, unstyled Typography dump. */}
          <Alert severity="warning" sx={{ mb: reauthMessage ? 1.5 : 0 }}>
            The connection to {channelName} needs to be re-authorized before it can sync again.
          </Alert>
          {reauthMessage && (
            <Box>
              {/* PR #655 review (M1, Medium) — the toggle's label already changes text, but
                  nothing exposed the expanded/collapsed state or which region it controls to
                  assistive technology. aria-expanded mirrors the same boolean the label text
                  already reflects; aria-controls points at the Collapse region's own id below. */}
              <Button
                size="small"
                variant="text"
                onClick={() => setShowReauthDetails((prev) => !prev)}
                aria-expanded={showReauthDetails}
                aria-controls="reauth-technical-details"
                sx={{ textTransform: 'none', px: 0, mt: 1 }}
              >
                {showReauthDetails ? 'Hide technical details' : 'Show technical details'}
              </Button>
              <Collapse in={showReauthDetails} id="reauth-technical-details">
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {reauthMessage}
                </Box>
              </Collapse>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReauthPromptOpen(false)} disabled={reauthPopupLoading || isReauthorizing}>
            Cancel
          </Button>
          <Button
            onClick={handleReauthOAuth}
            color="primary"
            variant="contained"
            disabled={reauthPopupLoading || isReauthorizing}
            startIcon={(reauthPopupLoading || isReauthorizing) ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {reauthPopupLoading ? 'Waiting for sign-in...' : isReauthorizing ? 'Re-authorizing...' : 'Sign in again'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Disconnect Email Connection</DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          <Typography component="div" sx={{ color: 'text.primary' }}>
            Are you sure you want to disconnect the email connection for{' '}
            <strong>{channelName}</strong>?
            This action will stop email synchronization and may affect email functionality.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={isDisconnecting}
            startIcon={isDisconnecting ? <CircularProgress size={20} /> : <DisconnectIcon />}
          >
            {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retrain / classification training confirmation dialog */}
      <Dialog
        open={retrainDialogOpen}
        onClose={() => !retrainLoading && setRetrainDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={retrainLoading}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Label Re-classification
          <IconButton
            aria-label="Close"
            onClick={() => !retrainLoading && setRetrainDialogOpen(false)}
            disabled={retrainLoading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to remove and start the Label Re-classification from scratch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleRetrainTraining(false)}
            disabled={retrainLoading}
            variant="outlined"
          >
            No, Reclassify
          </Button>
          <Button
            onClick={() => handleRetrainTraining(true)}
            disabled={retrainLoading}
            variant="contained"
            color="primary"
          >
            {retrainLoading ? 'Starting…' : 'Yes, start training'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - for email channels */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return; // Prevent closing when clicking outside
          }
          setDeleteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>
          Delete Email Data
        </DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              {`Are you sure you want to delete all email data for "${channelName}"?`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action will permanently remove all emails, threads, and associated data. This cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Channel Modal - theme-aware; header, content, footer use same background as other modals */}
      {/* zIndex 1401: must be above the sidebar which is bumped to 1400 by the channel-settings CSS rule */}
      <Dialog
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setIsLegacySubtagMigration(false); setTagRows([{ id: generateId(), tag: '' }]); }}
        maxWidth="md"
        fullWidth
        sx={(muiTheme) => ({
          zIndex: 1401,
          '& .MuiDialog-paper': {
            backgroundColor: muiTheme.palette.background.paper,
            color: muiTheme.palette.text.primary,
            zIndex: 1401,
          },
          // Keep header, content, and footer same as paper so no color strip (match other modals)
          '& .MuiDialogTitle-root': {
            backgroundColor: 'inherit',
          },
          '& .MuiDialogContent-root': {
            backgroundColor: 'inherit',
          },
          '& .MuiDialogActions-root': {
            backgroundColor: 'inherit',
          },
          '& .MuiBackdrop-root': {
            zIndex: 1400,
          },
        })}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Edit Channel</Typography>
          <IconButton
            onClick={() => setEditModalOpen(false)}
            size="small"
            sx={modalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Channel Name - Readonly */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                {LanguageConstants.topic} Name
              </Typography>
              <TextField
                fullWidth
                value={channelName || ''}
                disabled
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>

            {/* Workspace Tag - Readonly */}
            {workspaceTag && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                  {LanguageConstants.group}
                </Typography>
                <TextField
                  fullWidth
                  value={workspaceTag}
                  disabled
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Box>
            )}

            {/* Tags & Sub tags Section — one row per tag */}
            <Box sx={{ mt: 1 }}>
              {isLegacySubtagMigration && (
                <Alert
                  severity="warning"
                  onClose={() => setIsLegacySubtagMigration(false)}
                  sx={{ mb: 2 }}
                >
                  Existing sub tags aren't yet assigned to specific tags — please review and adjust before saving.
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                Select tags &amp; sub tags for this channel{' '}
                <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tagRows.map((row, rowIndex) => {
                  const rowTag = row.tag;
                  const availableOptions = TAG_OPTIONS.filter(
                    opt => !tagRows.some((r, i) => i !== rowIndex && r.tag === opt)
                  );
                  return (
                    <Box key={row.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ width: 220, flexShrink: 0 }}>
                        <Autocomplete
                          options={availableOptions}
                          value={rowTag || null}
                          onChange={(_, newValue) => {
                            const newRows = [...tagRows];
                            newRows[rowIndex] = { ...newRows[rowIndex], tag: newValue ?? '' };
                            setTagRows(newRows);
                          }}
                          slotProps={{ popper: { sx: { zIndex: 1500 } } }}
                          renderInput={(params) => (
                            <TextField {...params} label="Select tag" />
                          )}
                          sx={{
                            '& .MuiInputBase-root': { cursor: 'text' },
                            '& .MuiAutocomplete-input': { cursor: 'text' },
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {rowTag ? (
                          <KeywordsMultiSelect
                            value={selectedKeywordsPerTag[rowTag] ?? []}
                            onChange={(tags) =>
                              setSelectedKeywordsPerTag((prev) => ({ ...prev, [rowTag]: tags }))
                            }
                            workspaceName={workspaceTag || ''}
                            emailId={getEmailId()}
                            categories={selectedCategories}
                            tag={rowTag}
                            disabled={false}
                            placeholder={`Add sub tags for ${rowTag}…`}
                            label={`Sub tags for ${rowTag}`}
                            data-testid={`edit-channel-keywords-multi-select-${rowTag}`}
                            popperZIndex={1500}
                            lazyFetch
                          />
                        ) : (
                          <TextField
                            disabled
                            fullWidth
                            label="Select a tag first"
                            sx={{ '& .MuiInputBase-root': { cursor: 'default' } }}
                          />
                        )}
                      </Box>
                      {tagRows.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newRows = tagRows.filter((_, i) => i !== rowIndex);
                            setTagRows(newRows.length > 0 ? newRows : [{ id: generateId(), tag: '' }]);
                          }}
                          sx={{ mt: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="text"
                onClick={() => setTagRows(prev => [...prev, { id: generateId(), tag: '' }])}
                disabled={tagRows.some(r => r.tag === '') || tagRows.filter(r => r.tag).length >= TAG_OPTIONS.length}
                sx={{ mt: 1 }}
              >
                Add more tags
              </Button>
            </Box>

            {/* Purpose of the Channel - matching AddSpaceModal */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {showDescriptionCards && !showDescriptionField
                    ? 'Select a description for your channel'
                    : 'Purpose of the Channel'}{' '}
                  <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
                </Typography>
                {/* Always labeled "View Suggestions" — no separate "Edit Description" label.
                    Always shown: with no description yet, before any suggestion has been
                    picked, and while the admin is manually typing. Clicking it while the
                    suggestion cards are shown returns to the editable field, which always
                    displays the current description content as-is (bound directly to the
                    description state below — never cleared or replaced by this toggle).
                    Disabled (not hidden) until at least one tag+subtag is selected — PR #603
                    review (N1): the fetch effect's own guard silently no-ops without a tag/
                    subtag, so clicking beforehand did nothing observable. Disabling it with an
                    explanatory tooltip makes that state visible instead of a silent no-op. */}
                <Tooltip
                  title={
                    hasTagAndSubtagForSuggestions
                      ? ''
                      : 'Select at least one tag and sub tag first to get description suggestions'
                  }
                >
                  <span>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleToggleDescriptionView}
                      disabled={loadingDescriptions || !hasTagAndSubtagForSuggestions}
                      sx={{ textTransform: 'none', minWidth: 'auto' }}
                    >
                      View Suggestions
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              
              {showDescriptionField && !showDescriptionCards ? (
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., This channel is for the HR support shared email inbox"
                    multiline
                    rows={4}
                    required
                    data-testid="edit-channel-description-input"
                    inputRef={descriptionTextareaRef}
                    InputLabelProps={{
                      required: false,
                    }}
                    sx={{
                      '& .MuiInputBase-inputMultiline': {
                        pr: 4.5,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                        right: descriptionHasVerticalScroll && descriptionScrollbarWidth > 0
                          ? DESCRIPTION_EXPAND_ICON_BASE_RIGHT +
                            descriptionScrollbarWidth +
                            DESCRIPTION_EXPAND_ICON_SCROLL_GAP
                          : DESCRIPTION_EXPAND_ICON_BASE_RIGHT,
                      zIndex: 1,
                    }}
                  >
                    <Tooltip title="Expand">
                      <IconButton
                        onClick={() => setIsDescriptionExpanded(true)}
                        size="small"
                        aria-label="Expand description"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <OpenInFullIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ) : (
                (showDescriptionCards || loadingDescriptions) && (
                  <DescriptionCards
                    descriptions={descriptionSuggestions}
                    loading={loadingDescriptions}
                    selectedDescription={description}
                    onSelect={handleDescriptionSelect}
                    onEdit={handleDescriptionEdit}
                    disabled={false}
                  />
                )
              )}
            </Box>

            {channelType === 'email' && channelId && (
              <BusinessRulesSection
                key={editSessionId}
                variant="edit"
                saveMode="deferred"
                suggestTrigger="open"
                channelId={channelId}
                workspaceName={workspaceTag || ''}
                channelDescription={description || currentDescription || ''}
                channelTags={selectedCategories}
                channelSubtags={flatSelectedKeywords}
                emailId={getEmailId()}
                initialBusinessRules={currentBusinessRules || ''}
                value={editBusinessRules}
                onRulesChange={setEditBusinessRules}
                isChannelAdmin={isChannelAdmin}
                required
                externalValidationResult={serverValidationResult}
                onValidationMetaChange={handleBusinessRulesValidationMetaChange}
                descriptionSelectionNonce={businessRulesFetchNonce}
                onBuildWorkflow={onBuildWorkflow}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateChannel}
            variant="contained"
            color="primary"
            disabled={
              isUpdating ||
              !description.trim() ||
              (channelType === 'email' &&
                editBusinessRules.trim() !== '' &&
                (!businessRulesCanUpdate ||
                  businessRulesValidationStatus === 'pending' ||
                  businessRulesValidationStatus === 'validating'))
            }
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
            sx={
              !isUpdating &&
              channelType === 'email' &&
              businessRulesValidationStatus === 'warnings'
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.2,
                    minWidth: 120,
                  }
                : undefined
            }
          >
            {isUpdating ? 'Updating...' : 'Update'}
            {!isUpdating &&
              channelType === 'email' &&
              businessRulesValidationStatus === 'warnings' && (
                <Box component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 400 }}>
                  with known gaps
                </Box>
              )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDescriptionExpanded}
        onClose={() => setIsDescriptionExpanded(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            color: 'text.primary',
          },
        }}
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            Purpose of the Channel
          </Typography>
          <IconButton
            onClick={() => setIsDescriptionExpanded(false)}
            size="small"
            aria-label="Close expanded description"
            sx={modalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={(muiTheme) => ({
            bgcolor: 'background.paper',
            paddingTop: `${muiTheme.spacing(3)} !important`,
          })}
        >
          <TextField
            fullWidth
            margin="normal"
            autoFocus
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., This channel is for the HR support shared email inbox"
            multiline
            minRows={14}
            required
            InputLabelProps={{
              required: false,
              shrink: true,
              sx: {
                bgcolor: 'background.paper',
                px: 0.5,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: 'background.paper' }}>
          <Button onClick={() => setIsDescriptionExpanded(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailConfigTab;
